import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { getVeriPage, STRAPI_URL } from '../services/api';
import ScrollReveal from '../components/ScrollReveal';
import Seo from '../components/Seo';
import DEFAULT_DATA from '../default_datas/pages/defaultVeri';

const resolveMediaUrl = (mediaField) => {
  if (!mediaField) return null;
  const media = Array.isArray(mediaField) ? mediaField[0] : mediaField;
  const url = media?.url;
  if (!url) return null;
  return url.startsWith('http') ? url : `${STRAPI_URL}${url}`;
};

const Hero = ({ data, tx, navigate }) => {
  const d = { ...DEFAULT_DATA.hero, ...data };
  const imageUrl = resolveMediaUrl(d.image);

  return (
    <section className="flex flex-col lg:flex-row items-center justify-between px-6 lg:px-28 py-10 lg:py-20 bg-[#f5faf9] gap-10 lg:gap-16">
      <div className="flex-1 flex justify-center w-full order-2 lg:order-1">
        <div className="relative w-full max-w-md aspect-video rounded-2xl overflow-hidden shadow-xl border border-[#e2e8f0] bg-[#0d2b22] flex items-center justify-center">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={tx(d.title, d.title_en)}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="material-symbols-outlined text-[#2a9d8f] text-6xl lg:text-8xl">
              lock
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 max-w-full lg:max-w-[520px] text-center lg:text-left order-1 lg:order-2">
        <span className="inline-block bg-[#e0f2ef] text-[#2a9d8f] text-[10px] lg:text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wide uppercase">
          {tx(d.badge, d.badge_en)}
        </span>

        <h1 className="text-3xl lg:text-[42px] font-bold leading-tight mb-5 text-[#0D3A2F]">
          {tx(d.title, d.title_en)}
        </h1>

        <p className="text-sm lg:text-[15px] leading-relaxed text-[#4a6b63] mb-8">
          {tx(d.description, d.description_en)}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
          <button
            onClick={() => navigate('/iletisim')}
            className="inline-flex items-center justify-center gap-2 bg-[#2a9d8f] text-white border-none px-7 py-3.5 rounded-xl text-sm lg:text-[15px] font-semibold cursor-pointer hover:bg-[#238a7e] transition-colors"
          >
            {tx(d.primaryButtonText, d.primaryButtonText_en)}
            <span className="material-symbols-outlined text-base">arrow_forward</span>
          </button>

          {d.secondaryButtonText && (
            <button
              onClick={() => navigate('/iletisim')}
              className="inline-flex items-center justify-center gap-2 bg-white text-[#0D3A2F] border border-[#e0eeeb] px-7 py-3.5 rounded-xl text-sm lg:text-[15px] font-semibold cursor-pointer hover:bg-[#f5faf9] transition-colors"
            >
              {tx(d.secondaryButtonText, d.secondaryButtonText_en)}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

const Stats = ({ data, tx }) => {
  const d = { stats: DEFAULT_DATA.stats.stats, ...data };

  return (
    <section className="grid grid-cols-2 lg:flex gap-4 lg:gap-6 px-6 lg:px-28 py-8 lg:py-10 bg-white">
      {d.stats?.map((s, i) => (
        <div
          key={i}
          className="flex-1 border border-[#e0eeeb] rounded-xl px-4 py-4 lg:px-7 lg:py-6 flex flex-col gap-1 lg:gap-1.5 shadow-sm"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] lg:text-xs font-semibold text-[#6b9e96] uppercase tracking-wide">
              {tx(s.title, s.title_en)}
            </span>
            {s.icon && (
              <div className="w-7 h-7 lg:w-9 lg:h-9 rounded-lg bg-[#e8f5f2] flex items-center justify-center">
                <span className="material-symbols-outlined text-sm lg:text-base text-[#2a9d8f]">
                  {s.icon}
                </span>
              </div>
            )}
          </div>
          <span className="text-2xl lg:text-4xl font-bold text-[#0D3A2F]">
            {tx(s.value, s.value_en) || s.value}
          </span>
          <span className="text-[10px] lg:text-xs text-[#2a9d8f] font-medium">
            {tx(s.subText || s.trend, s.subText_en || s.trend_en)}
          </span>
        </div>
      ))}
    </section>
  );
};

const GridSection = ({ data, tx }) => {
  const d = { ...DEFAULT_DATA.grid, ...data };

  return (
    <section className="px-6 lg:px-28 py-12 lg:py-20 bg-[#f8fafc]">
      <div className="max-w-6xl mx-auto flex flex-col lg:grid gap-8 lg:gap-10 items-start lg:grid-cols-[1.2fr_1.8fr]">

        {/* Sol Panel - Sınıflandırma */}
        <div className="bg-white border border-[#eef5f3] rounded-2xl lg:rounded-3xl p-6 lg:p-8 shadow-sm relative overflow-hidden group w-full">
          <div className="absolute -top-6 -right-6 text-[#e8f5f2] opacity-50 group-hover:scale-110 transition-transform duration-500 hidden lg:block">
            <span className="material-symbols-outlined text-[100px] font-light">pie_chart</span>
          </div>

          <div className="relative z-10 flex items-center gap-4 mb-6 pb-5 border-b border-[#f1f5f9]">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl bg-[#e8f5f2] text-[#15614F] flex items-center justify-center shadow-inner">
              <span className="material-symbols-outlined text-2xl lg:text-3xl font-bold">category</span>
            </div>
            <div className="flex flex-col">
              <h3 className="text-lg lg:text-xl font-black text-[#15614F] tracking-tight m-0">
                {tx(d.title, d.title_en)}
              </h3>
              <p className="text-[10px] text-[#94a3b8] font-semibold uppercase tracking-wider m-0">
                {tx('Genel Dağılım', 'General Distribution')}
              </p>
            </div>
          </div>

          <p className="relative z-10 text-sm lg:text-[15px] text-[#64748b] mb-6 lg:mb-8 leading-relaxed font-medium">
            {tx(d.description, d.description_en)}
          </p>

          <div className="relative z-10 flex flex-col gap-3 lg:gap-5">
            {d.classificationList?.map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 lg:gap-4 p-2 lg:p-3 rounded-xl hover:bg-[#f9fdfd] transition-colors border border-transparent hover:border-gray-50"
              >
                <div
                  className="w-3 h-3 lg:w-3.5 lg:h-3.5 rounded-full flex-shrink-0"
                  style={{ background: item.color || '#cbd5e1' }}
                />
                <span className="flex-1 text-sm lg:text-[15px] font-bold text-[#15614F]">
                  {tx(item.label, item.label_en)}
                </span>
                <span className="text-xs lg:text-[15px] font-extrabold text-[#64748b] bg-gray-50 px-2 lg:px-3 py-1 rounded-md">
                  {tx(item.count, item.count_en)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Sağ Panel - Özellik Kartları */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6 w-full">
          {d.features?.map((f, i) => (
            <div
              key={i}
              className="bg-white border border-[#eef5f3] rounded-2xl px-6 py-6 lg:px-7 lg:py-8 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col gap-4"
            >
              <div className="w-12 h-12 rounded-xl lg:rounded-2xl bg-[#e8f5f2] text-[#15614F] flex items-center justify-center shadow-sm">
                <span className="material-symbols-outlined text-2xl lg:text-4xl font-bold">
                  {f.icon || 'star'}
                </span>
              </div>

              <div className="flex flex-col gap-2">
                <h4 className="text-base lg:text-[17px] font-black text-[#15614F] m-0 tracking-tight">
                  {tx(f.Title || f.title, f.Title_en || f.title_en)}
                </h4>
                <p className="text-xs lg:text-[14px] leading-relaxed text-[#64748b] m-0 font-medium">
                  {tx(f.Description || f.description, f.Description_en || f.description_en)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TableSection = ({ data, tx, lang }) => {
  const d = { ...DEFAULT_DATA.table, ...data };

  const getBadgeClass = (classification) => {
    const text = classification?.toLowerCase() || '';
    if (text.includes('çok gizli') || text.includes('top secret')) return 'bg-red-50 text-red-600 border border-red-100';
    if (text.includes('gizli') || text.includes('confidential')) return 'bg-orange-50 text-orange-600 border border-orange-100';
    if (text.includes('kurum içi') || text.includes('internal')) return 'bg-green-50 text-green-600 border border-green-100';
    return 'bg-gray-50 text-gray-600 border border-gray-100';
  };

  const getTypeIcon = (type) => {
    const text = type?.toLowerCase() || '';
    if (text.includes('veritabanı') || text.includes('database')) return 'database';
    if (text.includes('bilgi') || text.includes('information')) return 'info_i';
    if (text.includes('doküman') || text.includes('document')) return 'description';
    return 'inventory_2';
  };

  return (
    <section className="px-6 lg:px-28 py-12 lg:py-20 bg-white">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl lg:text-3xl font-black text-[#15614F] tracking-tight mb-8 text-center lg:text-left">
          {tx(d.title, d.title_en)}
        </h2>

        {/* Masaüstü Tablo */}
        <div className="hidden lg:block border border-[#eef5f3] rounded-2xl overflow-hidden shadow-sm">
          <div
            className="grid px-6 py-4 bg-[#f8fafc] text-[11px] font-bold text-[#64748b] uppercase tracking-[1px] border-b border-[#eef5f3]"
            style={{ gridTemplateColumns: '0.4fr 2fr 1fr 1.2fr 1fr' }}
          >
            <span></span>
            <span>{tx('Varlık Adı', 'Asset Name')}</span>
            <span>{tx('Tür', 'Type')}</span>
            <span>{tx('Sınıflandırma', 'Classification')}</span>
            <span>{tx('Durum', 'Status')}</span>
          </div>
          {d.rows?.map((item, i) => (
            <div
              key={i}
              className="grid px-6 py-4 border-b last:border-none border-[#f1f5f9] items-center hover:bg-[#f9fdfd] transition-all group"
              style={{ gridTemplateColumns: '0.4fr 2fr 1fr 1.2fr 1fr' }}
            >
              <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gray-50 text-[#15614F] group-hover:bg-white transition-colors">
                <span className="material-symbols-outlined text-[20px]">
                  {getTypeIcon(tx(item.type, item.type_en))}
                </span>
              </div>
              <span className="text-[14px] font-bold text-[#15614F]">{tx(item.name, item.name_en)}</span>
              <span className="text-[13px] text-[#64748b] font-medium">{tx(item.type, item.type_en)}</span>
              <div>
                <span className={`inline-block text-[11px] font-extrabold px-3 py-1.5 rounded-lg tracking-wide ${getBadgeClass(tx(item.classification, item.classification_en))}`}>
                  {tx(item.classification, item.classification_en)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0fbd49] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0fbd49]"></span>
                </span>
                <span className="text-[13px] text-[#15614F] font-bold">{tx('Aktif', 'Active')}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Mobil Liste Görünümü */}
        <div className="lg:hidden flex flex-col gap-4">
          {d.rows?.map((item, i) => (
            <div key={i} className="bg-[#f8fafc] border border-[#eef5f3] rounded-xl p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white text-[#15614F] flex items-center justify-center shadow-sm">
                    <span className="material-symbols-outlined text-lg">{getTypeIcon(tx(item.type, item.type_en))}</span>
                  </div>
                  <span className="text-sm font-bold text-[#15614F]">{tx(item.name, item.name_en)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0fbd49]"></span>
                  <span className="text-[10px] font-bold text-[#15614F] uppercase">{tx('Aktif', 'Active')}</span>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-[11px] text-[#64748b] font-semibold">{tx(item.type, item.type_en)}</span>
                <span className={`text-[9px] font-black px-2 py-1 rounded-md tracking-wider uppercase ${getBadgeClass(tx(item.classification, item.classification_en))}`}>
                  {tx(item.classification, item.classification_en)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Veri = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const tx = (tr, en) => (lang === 'en' ? (en || tr) : tr);

  useEffect(() => {
    getVeriPage()
      .then((res) => {
        if (res) setPageData(res);
      })
      .catch((error) => {
        console.error('Veri page fetch error:', error);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-[#2a9d8f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const content = pageData?.pageContent || [];

  return (
    <div className="min-h-screen bg-white font-poppins overflow-x-hidden">
      <Seo
        title={lang === 'en' ? 'Data Asset Management | RUZY' : 'Veri ve Bilgi Varlığı Yönetimi | RUZY'}
        path="/veri"
      />

      {/* Render logic with Fallbacks if Strapi is empty */}
      {content.length > 0 ? (
        content.map((section, index) => (
          <ScrollReveal key={index}>
            {section.__component === 'sections.hero' && <Hero data={section} tx={tx} navigate={navigate} />}
            {section.__component === 'sections.stats-section' && <Stats data={section} tx={tx} />}
            {section.__component === 'sections.grid-section' && <GridSection data={section} tx={tx} />}
            {section.__component === 'sections.table-section' && <TableSection data={section} tx={tx} lang={lang} />}
          </ScrollReveal>
        ))
      ) : (
        <>
          <ScrollReveal><Hero data={{}} tx={tx} navigate={navigate} /></ScrollReveal>
          <ScrollReveal><Stats data={{}} tx={tx} /></ScrollReveal>
          <ScrollReveal><GridSection data={{}} tx={tx} /></ScrollReveal>
          <ScrollReveal><TableSection data={{}} tx={tx} lang={lang} /></ScrollReveal>
        </>
      )}
    </div>
  );
};

export default Veri;